import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, TextField, Button, Alert, Switch, FormControlLabel,
  Card, CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const AdminCategories = () => {
  const { t } = useTranslation('admin');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState({ open: false, mode: 'add', data: null });

  // 統計數據
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/categories');
      if (response.data.success) {
        const cats = response.data.categories || [];
        setCategories(cats);
        calculateStats(cats);
      }
    } catch (error) {
      toast.error(t('categories.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (cats) => {
    const total = cats.length;
    const active = cats.filter(c => c.is_active).length;
    const inactive = total - active;
    setStats({ total, active, inactive });
  };

  const handleCategorySubmit = async () => {
    const { mode, data } = categoryDialog;
    if (!data?.name?.trim()) {
      toast.error(t('categories.name_required'));
      return;
    }

    setSaving(true);
    try {
      if (mode === 'add') {
        await api.post('/settings/categories', data);
        toast.success(t('categories.added'));
      } else {
        await api.put(`/settings/categories/${data.id}`, data);
        toast.success(t('categories.updated'));
      }
      setCategoryDialog({ open: false, mode: 'add', data: null });
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || t('common.operation_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm(t('categories.confirm_delete'))) return;
    try {
      await api.delete(`/settings/categories/${id}`);
      toast.success(t('categories.deleted'));
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || t('categories.delete_failed'));
    }
  };

  const moveCategoryOrder = async (id, direction) => {
    const index = categories.findIndex(c => c.id === id);
    if ((direction === -1 && index === 0) || (direction === 1 && index === categories.length - 1)) return;

    const newCategories = [...categories];
    const temp = newCategories[index];
    newCategories[index] = newCategories[index + direction];
    newCategories[index + direction] = temp;

    const items = newCategories.map((c, i) => ({ id: c.id, sort_order: i + 1 }));
    try {
      await api.put('/settings/categories/batch/sort', { items });
      setCategories(newCategories.map((c, i) => ({ ...c, sort_order: i + 1 })));
      toast.success(t('categories.sort_updated'));
    } catch (error) {
      toast.error(t('categories.sort_failed'));
    }
  };

  const toggleCategoryStatus = async (category) => {
    try {
      await api.put(`/settings/categories/${category.id}`, {
        ...category,
        is_active: category.is_active ? 0 : 1
      });
      loadCategories();
      toast.success(category.is_active ? t('categories.disabled') : t('categories.enabled'));
    } catch (error) {
      toast.error(t('categories.status_update_failed'));
    }
  };

  const openAddDialog = () => {
    setCategoryDialog({
      open: true,
      mode: 'add',
      data: {
        name: '',
        description: '',
        is_active: 1,
        sort_order: categories.length + 1
      }
    });
  };

  const openEditDialog = (category) => {
    setCategoryDialog({
      open: true,
      mode: 'edit',
      data: { ...category }
    });
  };

  return (
    <Box>
      {/* 頂部統計 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('categories.all')}</Typography>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('categories.active')}</Typography>
              <Typography variant="h4" color="success.main">{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('categories.inactive')}</Typography>
              <Typography variant="h4" color="text.disabled">{stats.inactive}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 標題與操作 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('categories.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            {t('categories.add')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadCategories}
            disabled={loading}
          >
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* 說明 */}
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('categories.info_text')}
      </Alert>

      {/* 分類列表 */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={100}>{t('categories.sort')}</TableCell>
              <TableCell>{t('categories.name')}</TableCell>
              <TableCell>{t('categories.description')}</TableCell>
              <TableCell width={100} align="center">{t('common.status')}</TableCell>
              <TableCell width={150} align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat, index) => (
              <TableRow key={cat.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => moveCategoryOrder(cat.id, -1)}
                      disabled={index === 0}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveCategoryOrder(cat.id, 1)}
                      disabled={index === categories.length - 1}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontWeight="bold">{cat.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {cat.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {cat.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={cat.is_active ? t('common.enabled') : t('common.disabled')}
                    color={cat.is_active ? 'success' : 'default'}
                    size="small"
                    onClick={() => toggleCategoryStatus(cat)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEditDialog(cat)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleCategoryDelete(cat.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? t('common.loading') : t('categories.no_data')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分類編輯對話框 */}
      <Dialog
        open={categoryDialog.open}
        onClose={() => setCategoryDialog({ open: false, mode: 'add', data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {categoryDialog.mode === 'add' ? t('categories.add') : t('categories.edit')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('categories.name')}
                value={categoryDialog.data?.name || ''}
                onChange={(e) => setCategoryDialog(prev => ({
                  ...prev,
                  data: { ...prev.data, name: e.target.value }
                }))}
                required
                placeholder="例如：生活用品"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('categories.description')}
                multiline
                rows={3}
                value={categoryDialog.data?.description || ''}
                onChange={(e) => setCategoryDialog(prev => ({
                  ...prev,
                  data: { ...prev.data, description: e.target.value }
                }))}
                placeholder="簡述此分類的商品類型"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!categoryDialog.data?.is_active}
                    onChange={(e) => setCategoryDialog(prev => ({
                      ...prev,
                      data: { ...prev.data, is_active: e.target.checked ? 1 : 0 }
                    }))}
                  />
                }
                label={t('categories.enable_category')}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {t('categories.disabled_hint')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog({ open: false, mode: 'add', data: null })}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleCategorySubmit} disabled={saving}>
            {categoryDialog.mode === 'add' ? t('common.add') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCategories;
